import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Title,
    Paper,
    Group,
    Text,
    Badge,
    Divider,
    Flex,
    Box,
    Button,
    ThemeIcon,
    Skeleton,
    Alert
} from '@mantine/core';
import {
    IconReceipt,
    IconDownload,
    IconArrowLeft,
    IconPackage,
    IconTruckDelivery,
    IconCheck,
    IconX,
    IconRefresh,
    IconExclamationCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { PaymentService } from '../types';
import { formatDate, formatCurrency, formatPaymentState, getPaymentType } from '../utils/paymentUtils';
import '../styles/payment.css';
import { Payment } from '../types/models/PaymentTransaction';

const PaymentDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [payment, setPayment] = useState<Payment | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [downloading, setDownloading] = useState<boolean>(false);

    useEffect(() => {
        const fetchPaymentDetails = async () => {
            if (!id) return;

            try {
                setLoading(true);
                // In a real implementation, we'd fetch the payment details by ID
                // For now, we'll get all payments and filter the one we want
                const response = await PaymentService.getPaymentMyHistory();

                if (response.success && response.data) {
                    const foundPayment = response.data.find(p => p.id === Number(id));
                    if (foundPayment) {
                        setPayment(foundPayment);
                    } else {
                        notifications.show({
                            title: 'Error',
                            message: 'Payment not found',
                            color: 'red',
                        });
                        navigate('/billing');
                    }
                } else {
                    notifications.show({
                        title: 'Error',
                        message: 'Failed to fetch payment details',
                        color: 'red',
                    });
                }
            } catch (error) {
                console.error('Error fetching payment details:', error);
                notifications.show({
                    title: 'Error',
                    message: 'An error occurred while fetching payment details',
                    color: 'red',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPaymentDetails();
    }, [id, navigate]);

    const handleDownloadInvoice = async () => {
        if (!payment) return;

        try {
            setDownloading(true);

            // In a real implementation, we would call the API to get the invoice
            // For now, we'll use a sample URL
            // const response = await PaymentService.downloadInvoice(payment.transactionId);

            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Create a download link for the sample invoice
            const link = document.createElement('a');
            link.href = payment.invoiceUrl || '';
            link.target = '_blank';
            link.download = `invoice-${payment.transactionId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            notifications.show({
                title: 'Success',
                message: 'Invoice downloaded successfully',
                color: 'green',
            });
        } catch (error) {
            console.error('Error downloading invoice:', error);
            notifications.show({
                title: 'Error',
                message: 'Failed to download invoice',
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const getPaymentStatusColor = (status: string) => {
        const statusUpper = status.toUpperCase();
        if (statusUpper === 'CONFIRMED') return 'teal';
        if (statusUpper.includes('REFUND')) return 'blue';
        if (statusUpper === 'PENDING') return 'yellow';
        return 'red';
    };

    const getPaymentStatusIcon = (status: string) => {
        const statusUpper = status.toUpperCase();
        if (statusUpper === 'CONFIRMED') return <IconCheck size={16} />;
        if (statusUpper.includes('REFUND')) return <IconRefresh size={16} />;
        if (statusUpper === 'PENDING') return <IconReceipt size={16} />;
        return <IconX size={16} />;
    };

    if (loading) {
        return (
            <Container size="md" py="xl">
                <Group mb="xl">
                    <Button
                        variant="subtle"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button>
                </Group>
                <Skeleton height={40} width="70%" mb="xl" />
                <Paper withBorder p="xl">
                    <Skeleton height={30} mb="md" />
                    <Skeleton height={100} mb="md" />
                    <Skeleton height={80} />
                </Paper>
            </Container>
        );
    }

    if (!payment) {
        return (
            <Container size="md" py="xl">
                <Group mb="xl">
                    <Button
                        variant="subtle"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button>
                </Group>
                <Alert
                    icon={<IconExclamationCircle size={16} />}
                    title="Payment Not Found"
                    color="red"
                >
                    The requested payment could not be found. Please check the payment ID and try again.
                </Alert>
            </Container>
        );
    }

    const paymentDate = new Date(payment.created_at);
    const paymentType = getPaymentType({
        shipmentOrderId: payment.shipmentOrderId,
        packageId: payment.packageId,
        combinedPackageId: payment.combinedPackageId,
    });
    const isSuccessful = payment.state.toUpperCase() === 'CONFIRMED';

    return (
        <Container size="md" py="xl">
            <Group mb="xl">
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate(-1)}
                >
                    Back to Payments
                </Button>
            </Group>

            <Title order={2} mb="md">Payment Details</Title>
            <Paper withBorder p="xl" radius="md">
                <Flex justify="space-between" mb="xl">
                    <Box>
                        <Text size="sm" color="dimmed">Transaction ID</Text>
                        <Text fw={500}>{payment.transactionId}</Text>
                    </Box>

                    <Badge
                        size="lg"
                        color={getPaymentStatusColor(payment.state)}
                        leftSection={getPaymentStatusIcon(payment.state)}
                    >
                        {formatPaymentState(payment.state)}
                    </Badge>
                </Flex>

                <Group mb="lg" grow>
                    <Box>
                        <Text size="sm" color="dimmed">Date</Text>
                        <Text>{formatDate(paymentDate, 'long')}</Text>
                    </Box>

                    <Box>
                        <Text size="sm" color="dimmed">Amount</Text>
                        <Text fw={700} size="xl">{formatCurrency(payment.amount, 'USD')}</Text>
                    </Box>
                </Group>

                <Divider my="lg" />

                <Box mb="lg">
                    <Text size="sm" color="dimmed" mb="xs">Payment Type</Text>
                    <Flex align="center" gap="md">
                        <ThemeIcon
                            size="lg"
                            variant="light"
                            color={payment.shipmentOrderId ? 'blue' : 'green'}
                            radius="xl"
                        >
                            {payment.shipmentOrderId ? <IconTruckDelivery size={20} /> : <IconPackage size={20} />}
                        </ThemeIcon>
                        <div>
                            <Text fw={500}>{paymentType}</Text>
                            {payment.shipmentOrderId && (
                                <Text size="sm">Shipment ID: {payment.shipmentOrderId}</Text>
                            )}
                            {payment.packageId && (
                                <Text size="sm">Package ID: {payment.packageId}</Text>
                            )}
                            {payment.combinedPackageId && (
                                <Text size="sm">Combined Package ID: {payment.combinedPackageId}</Text>
                            )}
                        </div>
                    </Flex>
                </Box>

                <Divider my="lg" />

                {isSuccessful && (
                    <Box>
                        <Text size="sm" color="dimmed" mb="md">Invoice</Text>
                        <Button
                            leftSection={<IconDownload size={16} />}
                            onClick={handleDownloadInvoice}
                            loading={downloading}
                        >
                            Download Invoice
                        </Button>
                    </Box>
                )}

                {!isSuccessful && (
                    <Alert color="yellow" title="Invoice Unavailable">
                        Invoices are only available for confirmed payments.
                    </Alert>
                )}
            </Paper>
        </Container>
    );
};

export default PaymentDetailPage;
